import styles from "./attaching-objects-to-task-modal.module.css";

import { useState, useEffect, useMemo, useRef } from "react";
import {
  Modal,
  Form,
  Typography,
  Button,
  Tag,
  Tooltip,
  Flex,
  InputNumber,
  Input,
  Row,
  Col,
  Divider,
} from "antd";

import CustomSelect from "@/components/inputs/CustomSelect";

import CloseIcon from "@/icons/CloseIcon";
import PlusIcon from "@/icons/PlusIcon";
import NestedSourcesBlock from "./components/NestedCandidates";

import {
  getAttachableObjects,
  getNestedCandidates,
  attachResources,
} from "@/api/tasks";
import {
  getDefinitionOptions,
  getItem as getDefinition,
} from "@/api/definitions";

const { Text } = Typography;

const STATUS_OPTIONS = [
  {
    value: "На согласовании",
    label: "На согласовании",
  },
  {
    value: "Отклонен",
    label: "Отклонен",
  },
  {
    value: "Согласован",
    label: "Согласован",
  },
  {
    value: "В обработке",
    label: "В обработке",
  },
  {
    value: "Забронирован",
    label: "Забронирован",
  },
  {
    value: "Выделен",
    label: "Выделен",
  },
  {
    value: "Освобожден",
    label: "Освобожден",
  },
];
const DEFAULT_STATUS = STATUS_OPTIONS[0].value;

const FORM_INITIAL_VALUES = {
  definition_uuid: null,
  quantity: 0,
  status: DEFAULT_STATUS,
  comment: null,
  objects: [],
};

const buildObjectOptions = (objects = [], selectedUuids = []) =>
  objects.map((obj) => ({
    value: obj.uuid,
    disabled: selectedUuids.includes(obj.uuid),
    label: (
      <div className={styles.objectOptionLabel}>
        <span className={styles.objectOptionName}>{obj.name}</span>

        {obj.unit?.name && (
          <Tooltip title={obj.unit?.name}>
            <Tag className={styles.objectOptionUnitTag}>{obj.unit.name}</Tag>
          </Tooltip>
        )}
      </div>
    ),
    searchLabel: `${obj.name} ${obj.unit?.name ?? ""}`,
  }));

const mapObjectsToForm = (objects = []) => {
  return objects.map((obj) => {
    const grouped = {};

    (obj.nested_resources || []).forEach((res) => {
      const key = res.source_field_uuid;

      if (!grouped[key]) {
        grouped[key] = {
          field_uuid: key,
          resources: [],
        };
      }

      grouped[key].resources.push({
        object_uuid: res.object_uuid,
        quantity: res.quantity,
      });
    });

    return {
      object_uuid: obj.object_uuid,
      quantity: obj.quantity,
      sources: Object.values(grouped),
    };
  });
};

const mapObjectsToSave = (objects = []) => {
  return objects.map((obj) => {
    const nested_resources = [];

    (obj.sources || []).forEach((source) => {
      const source_field_uuid = source.field_uuid;

      (source.resources || []).forEach((res) => {
        if (!res.object_uuid) return;

        nested_resources.push({
          source_field_uuid,
          object_uuid: res.object_uuid,
          quantity: res.quantity,
        });
      });
    });

    return {
      object_uuid: obj.object_uuid,
      quantity: obj.quantity,
      nested_resources,
    };
  });
};

// Окно для прикрепления ресурсов к задаче
const AttachingObjectsToTaskModal = ({
  attachedDefinitions = [],
  value = null,
  task_uuid = null,
  visible,
  onSave,
  onCancel,
  width = 600,
  isAdmins = false,
}) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [definitionsOptions, setDefinitionsOptions] = useState([]);
  const [objectsOptionsList, setObjectsOptionsList] = useState([]);
  const [tableOptions, setTableOptions] = useState([]);
  const [relatedOptionsMap, setRelatedOptionsMap] = useState({});

  const definitionUuid = Form.useWatch("definition_uuid", form);
  const objects = Form.useWatch("objects", form);

  const [measure_unit, setMeasureUnit] = useState(null);

  const isNew = useMemo(() => !value, [value]);

  useEffect(() => {
    fetchDefinitionsOptions();
    // eslint-disable-next-line
  }, []);

  const fetchDefinitionsOptions = async () => {
    try {
      const definitions = await getDefinitionOptions();
      setDefinitionsOptions(
        definitions.filter(
          ({ type, uuid }) =>
            ["repository", "object"].includes(type) &&
            (!isNew || !attachedDefinitions.includes(uuid)),
        ),
      );
    } catch (error) {
      setError(error.message);
    }
  };

  useEffect(() => {
    const fetchObjectsOptionsForField = async (definition_uuid) => {
      if (!definition_uuid) return;

      try {
        const [objects, definition] = await Promise.all([
          getAttachableObjects({
            task_uuid,
            definition_uuid,
          }),
          getDefinition(definitionUuid),
        ]);
        const tables =
          definition?.fields?.self?.filter((f) => f.type === "table") || [];

        setTableOptions(
          tables.map((t) => ({
            value: t.uuid,
            label: t.name,
          })),
        );

        setObjectsOptionsList(objects);
      } catch (error) {
        setError(error.message);
      }
    };

    if (definitionUuid) {
      fetchObjectsOptionsForField(definitionUuid);

      if (definitionsOptions?.length)
        setMeasureUnit(
          definitionsOptions.find((def) => def.uuid === definitionUuid)
            ?.measure_unit || "",
        );
    }
  }, [definitionUuid, definitionsOptions, task_uuid]);

  const fetchedKeysRef = useRef(new Set());

  const valueNestedMap = useMemo(() => {
    const map = new Map();

    (value?.objects || []).forEach((obj) => {
      (obj.nested_resources || []).forEach((res) => {
        const key = `${obj.object_uuid}_${res.source_field_uuid}`;

        if (!map.has(key)) {
          map.set(key, []);
        }

        map.get(key).push({
          value: res.object_uuid,
          label: res.name || res.object_uuid,
          searchLabel: res.name || res.object_uuid,
        });
      });
    });

    return map;
  }, [value]);

  useEffect(() => {
    const loadRelated = async () => {
      if (!objects?.length) return;

      const requests = [];

      for (const obj of objects) {
        const objectUuid = obj?.object_uuid;
        const sources = obj?.sources || [];

        if (!objectUuid) continue;

        for (const source of sources) {
          const tableFieldName = source?.field_uuid;
          if (!tableFieldName) continue;

          const key = `${objectUuid}_${tableFieldName}`;

          if (fetchedKeysRef.current.has(key)) continue;

          fetchedKeysRef.current.add(key);

          requests.push(
            await getNestedCandidates(
              {
                task_uuid,
                parent_definition_uuid: definitionUuid,
                parent_object_uuid: objectUuid,
                source_field_uuid: tableFieldName,
              },
              isAdmins,
            )
              .then((nestedCandidates) => ({
                key,
                options: nestedCandidates.map((row) => ({
                  value: row.uuid,
                  label: (
                    <div className={styles.objectOptionLabel}>
                      <span className={styles.objectOptionName}>
                        {row.name}
                      </span>

                      {row.unit?.name && (
                        <Tooltip title={row.unit?.name}>
                          <Tag className={styles.objectOptionUnitTag}>
                            {row.unit.name}
                          </Tag>
                        </Tooltip>
                      )}
                    </div>
                  ),
                  searchLabel: `${row.name} ${row.unit?.name ?? ""}`,
                })),
              }))
              .catch((e) => {
                console.error(e);
                return null;
              }),
          );
        }
      }

      if (!requests.length) return;

      const results = await Promise.all(requests);

      setRelatedOptionsMap((prev) => {
        const newMap = { ...prev };

        results.forEach((res) => {
          if (!res) return;

          const existing = res.options || [];
          const valueOptions = valueNestedMap.get(res.key) || [];

          const mergedMap = new Map();

          valueOptions.forEach((opt) => {
            mergedMap.set(opt.value, opt);
          });

          existing.forEach((opt) => {
            mergedMap.set(opt.value, opt);
          });

          newMap[res.key] = Array.from(mergedMap.values());
        });

        return newMap;
      });
    };

    loadRelated();
  }, [objects, definitionUuid, isAdmins, task_uuid, valueNestedMap]);

  const objectsOptions = useMemo(() => {
    const selected = objects || [];

    // Прикрепленные объекты могут быть в виде массива строк (uuid) или объектов (при редактировании), поэтому извлекаем uuid для сравнения с полем value в options, чтобы исключить уже прикрепленные объекты из списка доступных для выбора
    const selectedUuids = selected
      .map((obj) => (typeof obj === "string" ? obj : obj?.object_uuid))
      .filter(Boolean);

    const baseOptions = buildObjectOptions(objectsOptionsList, selectedUuids);

    const existingOptionValues = new Set(
      objectsOptionsList?.map((o) => o.uuid),
    );

    const valueObjectsMap = new Map(
      (value?.objects || []).map((obj) => [obj.object_uuid, obj]),
    );

    // выводим те, что были добавлены в расширенном режиме, который потом закрыли
    const missingOptions = selectedUuids
      .filter((uuid) => !existingOptionValues.has(uuid))
      .map((uuid) => {
        const objFromValue = valueObjectsMap.get(uuid);

        return {
          value: uuid,
          label: objFromValue?.name || uuid,
          searchLabel: objFromValue?.name || uuid,
        };
      });

    const map = new Map();
    missingOptions.forEach((opt) => {
      map.set(opt.value, opt);
    });
    baseOptions.forEach((opt) => {
      map.set(opt.value, opt);
    });

    return Array.from(map.values());
  }, [objects, objectsOptionsList, value]);

  const handleCancel = () => {
    form.resetFields();
    setError(null);
    onCancel();
  };

  const handleSave = async (values) => {
    setError(null);
    setIsLoading(true);

    const res = {
      task_uuid: task_uuid,
      type: {
        definition_uuid: values.definition_uuid,
        quantity: values.quantity,
        status: values.status,
        comment: values.comment,
      },
      objects: mapObjectsToSave(values.objects),
    };

    if (!isNew) res.type.uuid = value.type?.uuid;

    try {
      await attachResources(isNew ? "POST" : "PUT", res);

      onSave();
      form.resetFields();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onOk = () =>
    form
      .validateFields()
      .then((values) => {
        handleSave(values);
      })
      .catch((info) => {
        console.log("Ошибка валидации:", info);
      });

  return (
    <Modal
      maskClosable={false}
      title={isNew ? "Добавить ресурсы в задачу" : "Редактирование ресурсов"}
      open={visible}
      width={width}
      onOk={onOk}
      onCancel={handleCancel}
      okText={isNew ? "Добавить" : "Сохранить"}
      cancelText="Отмена"
      okButtonProps={{ loading: isLoading }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={
          value
            ? {
                definition_uuid: value.type.definition.uuid,
                quantity: value.type.quantity,
                status: value.type.status,
                comment: value.type.comment,
                objects: mapObjectsToForm(value.objects),
              }
            : FORM_INITIAL_VALUES
        }
      >
        <Form.Item
          name="definition_uuid"
          label="Тип ресурса"
          rules={[{ required: true, message: "Выберите тип ресурса" }]}
        >
          <CustomSelect
            disabled={!isNew}
            placeholder="Начните вводить"
            options={definitionsOptions}
            showSearch
            fieldNames={{ value: "uuid", label: "name" }}
            optionFilterProp="name"
            onChange={() => {
              form.setFieldsValue({
                quantity: 0,
                status: DEFAULT_STATUS,
                comment: null,
                objects: [],
              });
            }}
          />
        </Form.Item>
        {definitionUuid && (
          <>
            <Row gutter={{ xs: 10, sm: 10, md: 20, lg: 20 }}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="quantity"
                  label={`Всего ресурсов потребуется ${measure_unit ? `(${measure_unit})` : ""}`}
                >
                  <InputNumber min={0} className="w-100" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="status"
                  label="Статус"
                  rules={[{ required: true, message: "Выберите статус" }]}
                >
                  <CustomSelect
                    placeholder="Выберите статус"
                    options={STATUS_OPTIONS}
                    allowClear={false}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="comment" label="Комментарий">
              <Input.TextArea rows={3} />
            </Form.Item>

            <Form.List name="objects">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field) => (
                    <div key={field.key}>
                      <Row key={`${field.key}-divider1`}>
                        <Divider
                          style={{
                            borderColor: "var(--color-gray)",
                            margin: "20px 0",
                          }}
                        />
                      </Row>
                      <Row
                        key={field.key}
                        gutter={{ xs: 10, sm: 10, md: 20, lg: 20 }}
                        align="middle"
                      >
                        <Col xs={24} sm={15}>
                          <Form.Item
                            name={[field.name, "object_uuid"]}
                            label="Ресурс"
                            rules={[
                              { required: true, message: "Выберите ресурс" },
                            ]}
                          >
                            <CustomSelect
                              placeholder="Начните вводить"
                              options={objectsOptions}
                              showSearch
                              allowClear
                              optionLabelProp="label"
                              optionFilterProp="searchLabel"
                              onChange={(value) => {
                                const currentObjects =
                                  form.getFieldValue("objects") || [];

                                const newObjects = [...currentObjects];

                                if (newObjects[field.name]) {
                                  newObjects[field.name] = {
                                    ...newObjects[field.name],
                                    object_uuid: value || null,
                                    sources: [],
                                  };
                                }

                                form.setFieldsValue({
                                  objects: newObjects,
                                });
                              }}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={9}>
                          <Flex justify="space-between" align="center" gap={10}>
                            <Form.Item
                              name={[field.name, "quantity"]}
                              label={`Количество ${measure_unit ? `(${measure_unit})` : ""}`}
                              className="w-100"
                            >
                              <InputNumber min={1} className="w-100" />
                            </Form.Item>

                            <Button
                              type="text"
                              icon={<CloseIcon />}
                              onClick={() => remove(field.name)}
                            />
                          </Flex>
                        </Col>
                      </Row>

                      {/* Связанные ресурсы */}

                      <Form.List name={[field.name, "sources"]}>
                        {(
                          sourceFields,
                          { add: addSource, remove: removeSource },
                        ) => {
                          const parentObjectUuid = form.getFieldValue([
                            "objects",
                            field.name,
                            "object_uuid",
                          ]);

                          const hasTables = tableOptions?.length > 0;

                          const canAddSource = !!parentObjectUuid && hasTables;
                          return (
                            <>
                              {sourceFields.map((sourceField) => (
                                <div key={sourceField.key}>
                                  <NestedSourcesBlock
                                    field={field}
                                    sourceField={sourceField}
                                    form={form}
                                    tableOptions={tableOptions}
                                    relatedOptionsMap={relatedOptionsMap}
                                    removeSource={removeSource}
                                  />
                                </div>
                              ))}
                              {canAddSource && (
                                <Button
                                  type="dashed"
                                  icon={<PlusIcon />}
                                  block
                                  className={styles.addObjectButton}
                                  onClick={() =>
                                    addSource({ resources: [{ quantity: 1 }] })
                                  }
                                >
                                  Cвязанный ресурс
                                </Button>
                              )}
                            </>
                          );
                        }}
                      </Form.List>
                    </div>
                  ))}

                  {fields.length > 0 && (
                    <Divider
                      style={{
                        borderColor: "var(--color-gray)",
                        margin: "20px 0",
                      }}
                    />
                  )}

                  {fields.length < objectsOptions.length && (
                    <Button
                      type="dashed"
                      icon={<PlusIcon />}
                      onClick={() => add({ quantity: 1 })}
                      block
                      className={styles.addObjectButton}
                    >
                      Новый ресурс
                    </Button>
                  )}

                  {objectsOptions?.length === 0 && (
                    <Flex vertical align="center">
                      <span style={{ color: "#777" }}>
                        Отсутствуют ресурсы для добавления
                      </span>
                    </Flex>
                  )}
                </>
              )}
            </Form.List>
          </>
        )}

        {error && <Text type="danger">{error}</Text>}
      </Form>
    </Modal>
  );
};

export default AttachingObjectsToTaskModal;
